/* Question-first reading: the existing portal owns capture and email delivery. */
(() => {
  'use strict';
  if (customElements.get('cosmic-free-question')) return;

  class CosmicFreeQuestion extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.phase = this.phase || 'question';
      this.busy = false;
      this.closing = false;
      this.classList.remove('cmq-closing');
      const signal = this.controller.signal;
      this.addEventListener('click', event => this.click(event), { signal });
      this.addEventListener('submit', event => {
        if (event.target.matches('[data-cmq-question-form]')) {
          event.preventDefault(); this.next();
        } else if (event.target.matches('[data-cmq-details-form]')) {
          event.preventDefault(); this.submit();
        }
      }, { signal });
      this.addEventListener('input', event => {
        if (event.target.matches('[name="question"]')) {
          this.syncQuestion(); this.started();
        } else if (event.target.matches('[name="name"]')) this.syncName();
        if (event.target.matches('input,textarea')) this.clearErrors();
      }, { signal });
      this.setPhase(this.phase);
      this.setBusy(false);
      this.syncQuestion();
      this.syncName();
      this.dataset.ready = 'true';
    }

    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.request?.abort(); this.request = null;
      clearTimeout(this.requestTimeout);
      clearTimeout(this.transitionTimer);
      clearInterval(this.retryTimer);
      this.busy = false;
      this.closing = false;
    }

    $(selector) { return this.querySelector(selector); }
    all(selector) { return this.querySelectorAll(selector); }
    event(name, details = {}) {
      const hook = ['question', 'crossroads', 'curiosity'].includes(this.dataset.hook) ? this.dataset.hook : 'question';
      window.dataLayer = window.dataLayer || [];
      if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: name, funnel: 'free_question', hook, ...details });
    }
    announce(message) { this.$('[data-cmq-status]').textContent = message; }
    started() {
      if (this.hasStarted) return;
      this.hasStarted = true;
      this.event('question_started');
    }

    click(event) {
      if (event.target.closest('[data-paid-offer-link]') && this.phase === 'receipt') {
        this.event('upsell_clicked', { cohort: this.cohort || 'new', placement: 'confirmation' });
      }
      const button = event.target.closest('button');
      if (!button || !this.contains(button) || button.disabled || this.busy || this.closing) return;
      if (button.hasAttribute('data-cmq-prompt')) {
        this.$('[name="question"]').value = button.dataset.cmqPrompt;
        this.syncQuestion(); this.started();
        this.announce('Question added. You can edit it before continuing.');
      } else if (button.hasAttribute('data-cmq-back')) {
        this.clearErrors(); this.setPhase('question', true);
      } else if (button.hasAttribute('data-cmq-edit-email')) {
        this.clearErrors(); this.setPhase('details', true);
        this.$('[name="email"]').focus({ preventScroll: true });
      }
    }

    syncQuestion() {
      const field = this.$('[name="question"]');
      this.$('[data-cmq-count]').textContent = `${field.value.length} / 600`;
      if (this.phase === 'question') {
        field.style.height = 'auto';
        field.style.height = `${Math.max(56, field.scrollHeight)}px`;
      }
      this.all('[data-cmq-prompt]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.cmqPrompt === field.value)));
      this.$('[data-cmq-quote]').textContent = field.value.trim() || 'A little clarity on what comes next.';
    }
    syncName() {
      const name = this.$('[name="name"]').value.trim();
      this.$('[data-cmq-to]').textContent = name ? `For ${name}` : 'For your next chapter';
    }
    clearErrors() {
      this.all('[aria-invalid]').forEach(field => {
        field.removeAttribute('aria-invalid'); field.removeAttribute('aria-describedby');
      });
      this.showError('');
    }
    showError(message, field) {
      const error = this.$('[data-cmq-step="details"] [role="alert"]');
      error.textContent = message;
      error.hidden = !message;
      if (field) {
        field.setAttribute('aria-invalid', 'true');
        field.setAttribute('aria-describedby', error.id);
        field.focus();
      }
    }

    next() {
      if (this.phase !== 'question' || this.busy || this.closing) return;
      this.closing = true;
      this.$('[data-cmq-question-form] [data-cmq-advance]').disabled = true;
      this.syncQuestion();
      this.classList.add('cmq-closing');
      const lifecycle = this.controller;
      this.transitionTimer = setTimeout(() => {
        if (!this.isConnected || this.controller !== lifecycle) return;
        this.closing = false;
        this.classList.remove('cmq-closing');
        this.setPhase('details', true);
        this.setBusy(false);
        if (!this.formViewed) {
          this.formViewed = true;
          this.event('form_viewed', { question_present: Boolean(this.$('[name="question"]').value.trim()) });
        }
      }, matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 420);
    }

    setPhase(phase, focus = false) {
      this.phase = phase;
      this.all('[data-cmq-step]').forEach(panel => { panel.hidden = panel.dataset.cmqStep !== phase; });
      if (phase === 'question') this.syncQuestion();
      if (focus) {
        const title = this.$(`[data-cmq-step="${phase}"] h1, [data-cmq-step="${phase}"] h2`);
        title?.focus({ preventScroll: true });
        this.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }
    }

    setBusy(busy) {
      this.busy = busy;
      this.setAttribute('aria-busy', String(busy));
      this.all('input,textarea,button').forEach(element => { element.disabled = busy; });
      this.tickRetry();
    }
    tickRetry() {
      clearInterval(this.retryTimer);
      const update = () => {
        if (!this.isConnected) return;
        const remaining = Math.max(0, Math.ceil(((this.retryAt || 0) - Date.now()) / 1000));
        this.$('[data-cmq-details-form] [data-cmq-advance]').disabled = this.busy || remaining > 0;
        this.$('[data-cmq-submit-label]').textContent = this.busy ? 'Saving your request…' : remaining > 0 ? `Try again in ${remaining}s` : 'Send my free reading';
        if (!remaining) clearInterval(this.retryTimer);
      };
      update();
      if ((this.retryAt || 0) > Date.now()) this.retryTimer = setInterval(update, 1000);
    }

    async submit() {
      if (this.busy || this.phase !== 'details' || Date.now() < (this.retryAt || 0)) return;
      this.clearErrors();
      const nameField = this.$('[name="name"]');
      const emailField = this.$('[name="email"]');
      const name = nameField.value.trim().slice(0, 80);
      const email = emailField.value.trim();
      const question = this.$('[name="question"]').value.trim().slice(0, 600);
      if (!name) { this.showError('Add your first name so we know who the reading is for.', nameField); return; }
      if (!email || email.length > 254 || !emailField.validity.valid || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.showError('Check your email address so your reading can reach you.', emailField); return;
      }
      nameField.value = name; emailField.value = email;
      this.syncName();
      this.event('signup_submitted', { question_present: Boolean(question) });
      this.setBusy(true);
      const lifecycle = this.controller;
      const request = new AbortController();
      this.request = request;
      this.requestTimeout = setTimeout(() => request.abort(), 25000);
      try {
        const base = new URL(this.dataset.portalBase || 'https://portal.1727labs.com');
        if (base.protocol !== 'https:' || base.username || base.password) throw new Error('Invalid portal configuration');
        const response = await fetch(new URL('/api/free-reading/cosmicmonica', base), {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'omit',
          body: JSON.stringify({ name, email, ...(question ? { question } : {}) }), signal: request.signal
        });
        if (!this.isConnected || this.controller !== lifecycle) return;
        if (response.status === 429) {
          const header = response.headers.get('Retry-After');
          const seconds = /^\d+$/.test(header || '') ? Number(header) : (Date.parse(header) - Date.now()) / 1000;
          this.retryAt = Date.now() + Math.min(3600, Math.max(1, Number.isFinite(seconds) ? seconds : 60)) * 1000;
          this.showError('A few requests arrived together. Please wait a moment and try again.');
          this.event('signup_failed', { reason: 'rate_limit' });
          return;
        }
        if (response.status === 400) {
          this.setBusy(false);
          this.showError('Please check your email address and try again.', emailField);
          this.event('signup_failed', { reason: 'validation' });
          return;
        }
        if (!response.ok) throw new Error('Capture failed');
        const body = await response.json();
        if (!this.isConnected || this.controller !== lifecycle) return;
        if (typeof body?.freeReadingId !== 'string' || !body.freeReadingId.trim() || (body.duplicate !== undefined && typeof body.duplicate !== 'boolean')) throw new Error('Capture was not confirmed');
        this.$('[data-cmq-address]').textContent = email;
        this.$('[data-cmq-receipt-eyebrow]').textContent = body.duplicate ? 'Your reading request' : 'Something to look forward to';
        this.$('[data-cmq-receipt-title]').textContent = body.duplicate ? 'Your free reading is already requested.' : 'Your free reading is requested.';
        this.$('[data-cmq-confirm]').textContent = body.duplicate
          ? 'Look out for the reading from your original request, or check your inbox if it has already arrived.'
          : 'Your request is saved. Look out for your reading from CosmicMonica in around 40 minutes.';
        this.setPhase('receipt', true);
        const cohort = ['new', 'lapsed', 'active'].includes(body.cohort) ? body.cohort : 'new';
        this.cohort = cohort;
        this.event('signup_succeeded', { cohort, duplicate: body.duplicate === true, question_present: Boolean(question) });
        if (this.$('[data-paid-offer]')) this.event('upsell_presented', { cohort, placement: 'confirmation' });
        this.announce(body.duplicate ? 'Your free reading was already requested.' : 'Your free reading request is saved.');
      } catch (_) {
        if (this.isConnected && this.controller === lifecycle) {
          this.showError('We couldn’t confirm your request. Your details are still here. Please try again.');
          this.event('signup_failed', { reason: 'unconfirmed' });
        }
      } finally {
        if (this.controller === lifecycle) {
          clearTimeout(this.requestTimeout);
          this.request = null;
          if (this.isConnected) this.setBusy(false);
        }
      }
    }
  }
  customElements.define('cosmic-free-question', CosmicFreeQuestion);
})();
